import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const DESTINATIONS = ["JAKARTA", "EJIP", "TANGERANG", "JNT"];
const monthNow = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};
const rupiah = (value) => value == null ? "Perlu cek" : new Intl.NumberFormat("id-ID").format(value);

const ReceiptDeliveryListPage = () => {
  const [category, setCategory] = useState("C");
  const [month, setMonth] = useState(monthNow);
  const [destination, setDestination] = useState("JAKARTA");
  const [printFilter, setPrintFilter] = useState("UNPRINTED");
  const [selected, setSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadedKey, setLoadedKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  const key = `${category}|${month}`;

  const load = async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) return toast.error("Pilih bulan yang benar");
    setLoading(true);
    setLoadedKey("");
    try {
      const response = await axios.get("/api/reports/receipt-list", { params: { category, month } });
      setRows(response.data.data || []);
      setLoadedKey(key);
    } catch (error) {
      setRows([]);
      toast.error(error.response?.data?.detail || "Gagal memuat list Tanda Terima");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoadedKey(""); setRows([]); setSelected([]); }, [category, month]);
  useEffect(() => { setSelected([]); }, [destination]);

  const allInDestination = useMemo(() => rows.filter((row) => row.destination === destination), [rows, destination]);
  const visible = useMemo(() => allInDestination.filter((row) => {
    if (printFilter === "PRINTED" && !row.printed_at) return false;
    if (printFilter === "UNPRINTED" && row.printed_at) return false;
    const q = search.trim().toLowerCase();
    return !q || [row.invoice_number, row.customer_name, row.customer_id, row.customer_city]
      .some((value) => String(value || "").toLowerCase().includes(q));
  }), [allInDestination, search, printFilter]);
  const chosen = useMemo(() => allInDestination.filter((row) => selected.includes(row.invoice_number)), [allInDestination, selected]);
  const allVisibleSelected = visible.length > 0 && visible.every((row) => selected.includes(row.invoice_number));
  const toggleAll = () => setSelected((current) => allVisibleSelected
    ? current.filter((number) => !visible.some((row) => row.invoice_number === number))
    : [...new Set([...current, ...visible.map((row) => row.invoice_number)])]);

  const counts = useMemo(() => Object.fromEntries(DESTINATIONS.map((name) => [name,
    rows.filter((row) => row.destination === name).length])), [rows]);

  const update = async (row, change) => {
    setBusy(row.invoice_number);
    try {
      const response = await axios.patch(`/api/reports/receipt-list/${encodeURIComponent(row.invoice_number)}`, change);
      setRows((current) => current.map((item) => item.invoice_number === row.invoice_number ? {
        ...item,
        destination_override: response.data.destination_override,
        destination: response.data.destination_override || item.auto_destination,
      } : item));
      toast.success("List Tanda Terima diperbarui");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan perubahan");
    } finally {
      setBusy("");
    }
  };

  const download = async () => {
    if (loadedKey !== key) return;
    if (!chosen.length) return toast.error("Centang minimal satu invoice");
    const reprintCount = chosen.filter((row) => row.printed_at).length;
    if (reprintCount && !window.confirm(`${reprintCount} invoice sudah pernah dibuatkan Excel. Cetak ulang?`)) return;
    setDownloading(true);
    try {
      const response = await axios.post("/api/reports/receipt-list/excel", {
        category, month, destination, invoice_numbers: chosen.map((row) => row.invoice_number), allow_reprint: reprintCount > 0,
      }, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Tanda_Terima_${category}_${destination}_${month}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      const printedAt = new Date().toISOString();
      setRows((current) => current.map((row) => chosen.some((item) => item.invoice_number === row.invoice_number)
        ? { ...row, printed_at: row.printed_at || printedAt } : row));
      setSelected([]);
      toast.success(`${chosen.length} invoice masuk Excel dan ditandai sudah dibuat untuk cetak`);
    } catch (error) {
      toast.error("Gagal mengunduh Excel Tanda Terima");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-5">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #receipt-list-print, #receipt-list-print * { visibility: visible !important; }
        #receipt-list-print { position: absolute; left: 0; top: 0; width: 100%; }
        .receipt-list-no-print { display: none !important; }
        #receipt-list-print table { width: 100%; border-collapse: collapse; }
        #receipt-list-print th, #receipt-list-print td { border: 1px solid #333; padding: 6px; }
        #receipt-list-print thead { display: table-header-group; }
      }`}</style>
      <div className="receipt-list-no-print">
        <h1 className="text-2xl font-bold text-gray-900">List Tanda Terima</h1>
        <p className="text-gray-500 mt-1">Daftar pengantaran per nomor invoice. Invoice C, D, dan K dipisah.</p>
      </div>
      <div className="receipt-list-no-print bg-white border rounded-xl p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Jenis invoice</label>
            <div className="flex gap-2">
              {["C", "D", "K"].map((code) => <button type="button" key={code} onClick={() => setCategory(code)}
                className={`px-4 py-2 rounded-lg border ${category === code ? "bg-blue-600 text-white" : "bg-white"}`}>
                Invoice {code}</button>)}
            </div>
          </div>
          <label className="text-sm font-medium">Bulan invoice
            <input type="month" value={month} onChange={(event) => setMonth(event.target.value)}
              className="block mt-1 border rounded-lg p-2" />
          </label>
          <button type="button" onClick={load} disabled={loading}
            className="flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 disabled:opacity-50">
            <RefreshCw size={16} />{loading ? "Memuat..." : "Tampilkan List"}
          </button>
        </div>
        {loadedKey === key && <div className="flex flex-wrap gap-2">
          {DESTINATIONS.map((name) => <button type="button" key={name} onClick={() => { setDestination(name); setSearch(""); }}
            className={`px-4 py-2 rounded-lg border ${destination === name ? "bg-purple-600 text-white" : "bg-white"}`}>
            {name} ({counts[name]})</button>)}
        </div>}
        {loadedKey === key && <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">Cari invoice / perusahaan
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)}
              className="block mt-1 border rounded-lg p-2 w-64" />
          </label>
          <label className="text-sm">Status cetak
            <select value={printFilter} onChange={(event) => setPrintFilter(event.target.value)} className="block mt-1 border rounded-lg p-2">
              <option value="UNPRINTED">Belum tercetak</option>
              <option value="PRINTED">Sudah tercetak</option>
              <option value="ALL">Semua</option>
            </select>
          </label>
          <span className="text-sm pb-2">Terpilih: {chosen.length}</span>
          <button type="button" onClick={download} disabled={downloading || !chosen.length}
            className="flex items-center gap-2 bg-green-700 text-white rounded-lg px-4 py-2 disabled:opacity-50">
            <Download size={16} />Download Excel Terpilih</button>
        </div>}
      </div>
      {loadedKey === key && <div id="receipt-list-print" className="bg-white border rounded-xl overflow-hidden">
        <div className="p-4 flex justify-between gap-4">
          <div><h2 className="text-xl font-bold">{destination}</h2><p>Invoice {category} — {month}</p></div>
          <div>TANGGAL: {new Date().toLocaleDateString("id-ID")}</div>
        </div>
        <div className="max-h-[650px] overflow-auto print:overflow-visible print:max-h-none">
          <table className="min-w-[1250px] w-full text-sm print:min-w-0">
            <thead className="sticky top-0 bg-gray-100 z-10 print:static">
              <tr><th className="p-3 border-b receipt-list-no-print"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Pilih semua hasil terlihat" /></th>{["No.", "No. Invoice", "Cust Code", "Nama Perusahaan", "Kota", "Jumlah (Rp)", "Status cetak", "Yang menerima", "Tanggal", "Tujuan"].map((head) =>
                <th key={head} className={`p-3 text-left border-b whitespace-nowrap ${["Kota", "Tujuan"].includes(head) ? "receipt-list-no-print" : ""}`}>{head}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map((row, index) => <tr key={row.invoice_number} className="border-b hover:bg-gray-50">
                <td className="p-3 receipt-list-no-print"><input type="checkbox" checked={selected.includes(row.invoice_number)} onChange={() => setSelected((current) => current.includes(row.invoice_number) ? current.filter((number) => number !== row.invoice_number) : [...current, row.invoice_number])} aria-label={`Pilih ${row.invoice_number}`} /></td>
                <td className="p-3">{index + 1}</td>
                <td className="p-3 font-medium whitespace-nowrap">{row.invoice_number}</td>
                <td className="p-3">{row.customer_id}</td>
                <td className="p-3">{row.customer_name}</td>
                <td className="p-3 receipt-list-no-print">{row.customer_city || "-"}</td>
                <td className="p-3 text-right whitespace-nowrap">{rupiah(row.amount)}</td>
                <td className="p-3 whitespace-nowrap receipt-list-no-print">{row.printed_at ? "Sudah tercetak" : "Belum tercetak"}</td>
                <td className="p-3"> </td><td className="p-3"> </td>
                <td className="p-3 receipt-list-no-print">
                  <select value={row.destination_override || "AUTO"} disabled={busy === row.invoice_number}
                    onChange={(event) => update(row, { destination_override: event.target.value === "AUTO" ? null : event.target.value })}
                    className="border rounded-lg p-2">
                    <option value="AUTO">Otomatis: {row.auto_destination}</option>
                    {DESTINATIONS.map((name) => <option key={name} value={name}>{name}</option>)}
                  </select>
                </td>
              </tr>)}
              {!visible.length && <tr><td colSpan="11" className="p-8 text-center text-gray-500">Tidak ada invoice pada pilihan ini.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  );
};

export default ReceiptDeliveryListPage;
